import {
  createContext,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useContext,
  useState,
} from "react";
import { createPortal } from "react-dom";

import { cn } from "@src/shared/lib/utils";

interface AuthenticatedAppHeaderSlotContextValue {
  node: HTMLElement | null;
  setNode: Dispatch<SetStateAction<HTMLDivElement | null>>;
}

const AuthenticatedAppHeaderSlotContext =
  createContext<AuthenticatedAppHeaderSlotContextValue | null>(null);

export interface AuthenticatedAppHeaderSlotProviderProps {
  children: ReactNode;
}

export interface AuthenticatedAppHeaderProps {
  children: ReactNode;
}

export interface AuthenticatedAppHeaderSlotProps {
  className?: string;
}

export function AuthenticatedAppHeaderSlotProvider({
  children,
}: AuthenticatedAppHeaderSlotProviderProps) {
  const [node, setNode] = useState<HTMLDivElement | null>(null);

  return (
    <AuthenticatedAppHeaderSlotContext.Provider value={{ node, setNode }}>
      {children}
    </AuthenticatedAppHeaderSlotContext.Provider>
  );
}

export function AuthenticatedAppHeaderSlot({
  className,
}: AuthenticatedAppHeaderSlotProps) {
  const context = useContext(AuthenticatedAppHeaderSlotContext);

  if (!context) {
    throw new Error(
      "AuthenticatedAppHeaderSlot must be used within AuthenticatedAppHeaderSlotProvider",
    );
  }

  return (
    <div
      ref={context.setNode}
      className={cn(
        "empty:hidden flex min-h-12 shrink-0 flex-wrap items-center gap-3 border-b border-border bg-background px-4 py-2",
        className,
      )}
    />
  );
}

export function AuthenticatedAppHeader({
  children,
}: AuthenticatedAppHeaderProps) {
  const context = useContext(AuthenticatedAppHeaderSlotContext);

  if (!context) {
    throw new Error(
      "AuthenticatedAppHeader must be used within AuthenticatedAppHeaderSlotProvider",
    );
  }

  if (!context.node) return null;

  return createPortal(children, context.node);
}
